import React, { useState, useRef, useEffect } from "react";
import {
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Autocomplete,
  styled,
  Popper,
  Popover,
  List,
  ListItem,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

import { toast } from "react-toastify";
import { licensePlate } from "../style";
import trailerPlateIcon from "../../../assets/images/icons/trailerPlate.svg";
import truckPlateIcon from "../../../assets/images/icons/truckPlate.svg";
import modifyIcon from "../../../assets/images/icons/modifyIcon.svg";
import { useTranslation } from "react-i18next";
import { tripsService } from "../../../api/tripsService/tripsServices";
import { useAuth } from "../../../utils/hooks/useAuth";
import { ApprovalStatus, StatusOptions } from "../../../types/FilterEnums";
import { driverDrawer } from "../../fleetAndRoute/style";
import { fleetService } from "../../../api/fleetService/fleetServicesRegistered";
import TraceabilityPopover from '../../../components/TraceabilityPopover';

interface EditableLicensePlateProps {
  value: string;
  equipmentType: "TRAILER" | "TRUCK" ;
  onSave: (newValue: string) => void;
  tripId:string| undefined
  businessProfileName:any;
  approvalStatus:string
  vehiclePlates: string[]; 
  trailerPlates: string[];
  tripEndpoints:any;
  setDriver: React.Dispatch<React.SetStateAction<any | null>>;
  setErrorDriver: React.Dispatch<React.SetStateAction<string | null>>;
  setLoadingDriver: React.Dispatch<React.SetStateAction<boolean>>;
  status:string
  isUpdatable?: boolean
}
const EditableLicensePlate: React.FC<EditableLicensePlateProps> = ({
  value,
  equipmentType,
  onSave,
  tripId,
  businessProfileName,
  approvalStatus,
  vehiclePlates,
  trailerPlates,
  tripEndpoints,
  setDriver,
  setErrorDriver,
  setLoadingDriver,
  status,
  isUpdatable
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value);
  const [showDialog, setShowDialog] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [disableBlur, setDisableBlur] = useState(false);
  const [firstDriverSuccess, setFirstDriverSuccess] = useState<boolean | null>(null);
  const { t } = useTranslation();
  const handleEditClick = () => {
    setIsEditing(true);
  };
  const {user} = useAuth()
 const [legacyData,setLegacyData] = useState(businessProfileName)
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditedValue(event.target.value);
  };
  useEffect(()=>{
    console.log("firstDriverSuccess",firstDriverSuccess)
  },[firstDriverSuccess])

  const [traceabilityDialogOpen, setTraceabilityDialogOpen] = useState(false);
  const [traceabilityData, setTraceabilityData] = useState<{
    tripsInfo: Array<{ tmsObjectNumber: string; tripID: number }>;
    platform?: string;
    licensePlate: string;
  }>({ tripsInfo: [], licensePlate: "" });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

   const handleSave = async () => {
    try {
      setDisableBlur(true);
      setIsEditing(false);
      setShowDialog(false);
  
      // Chiamata API
      const response = await tripsService.updateLicensePlate({
        tripID: Number(tripId),
        licensePlate: editedValue,
        equipmentType,
        user: user?.email || user?.fullName || "-",
      });

      // Check traceability response
      if (response.isTraceability === false || (response.isTraceability === null && response.traceabilitySuccess === false)) {
        setTraceabilityData({ 
          tripsInfo: response.tripsInfo || [],
          platform: response.platform || '',
          licensePlate: editedValue
        });
        setTraceabilityDialogOpen(true);
        setAnchorEl(document.body);
      }
  
      // Aggiorniamo la UI
      onSave(response.licensePlate);
  
      // Chiama getFirstDriver solo per i TRUCK
      if (equipmentType === "TRUCK") {
        const fetchDriver = async () => {
    
          try {
            const legacyCode = legacyData[0]?.legacycode || "-";  // Sostituisci con il valore reale o trattino
            const company = legacyData[0]?.company || "-";        // Sostituisci con il valore reale o trattino
            const _tripId = tripId || "";
            const licensePlate = editedValue;
            const date = tripEndpoints[0].expectedArrival
              ? new Date(tripEndpoints[0].expectedArrival)
              : new Date(); // Se null, usa la data attuale
    
            const data = await fleetService.getFirstDriver(
              legacyCode,
              company,
              licensePlate,
              date,
              _tripId
            );
    
            // Aggiorna lo stato del successo di firstDriver
            setFirstDriverSuccess(data.success === true);
    
            // Se i dati non sono validi, usa valori di fallback (trattini)
            const driverData = {
              success: data.success === true,
              data: data.success === true ? data.data : {
                email: "-",
                firstName: "-",
                lastName: "-",
                phone: "-"
              }
            };
    
            setDriver(driverData);
    
          } catch (err) {
            // In caso di errore, imposta driver con i trattini e firstDriver come fallito
            setFirstDriverSuccess(false);
            setErrorDriver("Errore nel caricamento del driver.");
            setDriver({
              success: false,
              data: {
                email: "",
                firstName: "-",
                lastName: "-",
                phone: "-"
              }
            });
          } finally {
            setLoadingDriver(false);
          }
        };
    
        fetchDriver();
      }
  
      toast.success(t("screens.trips.licensePlate.success"), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
  
    } catch (error) {
      toast.error(t("screens.trips.licensePlate.error"), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setDisableBlur(false);
    }
  };
  
  const handleCancel = () => {
    setShowDialog(false);
    setIsEditing(false);
    setEditedValue(value);
    toast.info("Changes canceled", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (disableBlur) return;
  
  };

  const handleAutocompleteChange = (event: React.ChangeEvent<{}>, newValue: string | null) => {
    setEditedValue(newValue || '');
  
    setShowDialog(true);
  };
  

  // Icona basata sul tipo di equipaggiamento
  const renderIcon = () => {
    if (equipmentType === "TRUCK") {
      return (
        <img
          src={truckPlateIcon}
          alt="Contracts Icon"
          style={{ width: "25px", height: "25px" }}
        />
      );
    } else if (equipmentType === "TRAILER") {
      return (
        <img
          src={trailerPlateIcon}
          alt="Contracts Icon"
          style={{ width: "25px", height: "25px" }}
        />
      );
    }
    return null;
  };

  const availablePlates = equipmentType === "TRUCK" ? vehiclePlates : trailerPlates;
  const CustomPopper = styled(Popper)(driverDrawer.autocompleteOptions);

  // Determina il colore e lo spessore basato su firstDriver e valore della targa
  const getPlateStyle = () => {
    // Per i TRUCK: se firstDriver ha fallito (success: false), mostra rosso e grassetto
    if (equipmentType === "TRUCK" && firstDriverSuccess === false) {
      return {
        color: '#CF0A2C',
        fontWeight: 'bold',
        cursor: 'pointer',
        fontSize: '12px'
      };
    }
    
    // Se la targa è vuota o non valida, mostra rosso e grassetto (per tutti i tipi)
    if (!editedValue || editedValue === '' || editedValue === '-') {
      return {
        color: '#CF0A2C',
        fontWeight: 'bold',
        cursor: 'pointer',
        fontSize: '12px'
      };
    }
    
    // Altrimenti usa lo stile normale
    return {
      color: 'inherit',
      fontWeight: 'normal',
      cursor: 'pointer',
      fontSize: '12px'
    };
  };

  // Chiamata iniziale a getFirstDriver per determinare lo stato
  useEffect(() => {
    const checkFirstDriver = async () => {
      console.log("checkFirstDriver chiamato con:", {
        equipmentType,
        legacyData: legacyData?.length,
        editedValue,
        tripId,
        tripEndpoints: tripEndpoints?.length
      });
      
      // Solo per TRUCK e se abbiamo tutti i dati necessari e la targa non è vuota
      if (equipmentType === "TRUCK" && legacyData && legacyData.length > 0 && editedValue && editedValue !== '' && editedValue !== '-' && tripId && tripEndpoints && tripEndpoints.length > 0) {
        try {
          const legacyCode = legacyData[0]?.legacycode || "-";
          const company = legacyData[0]?.company || "-";
          const licensePlate = editedValue;
          const date = tripEndpoints[0].expectedArrival
            ? new Date(tripEndpoints[0].expectedArrival)
            : new Date();

          console.log("Chiamando getFirstDriver con:", {
            legacyCode,
            company,
            licensePlate,
            date,
            tripId
          });

          const data = await fleetService.getFirstDriver(
            legacyCode,
            company,
            licensePlate,
            date,
            tripId
          );

          console.log("Risposta getFirstDriver:", data);
          setFirstDriverSuccess(data.success === true);
        } catch (err) {
          console.error("Errore nel controllo iniziale di firstDriver:", err);
          setFirstDriverSuccess(false);
        }
      } else {
        console.log("Condizioni non soddisfatte per chiamare getFirstDriver o non è un TRUCK");
      }
    };

    checkFirstDriver();
  }, [equipmentType, legacyData, editedValue, tripId, tripEndpoints]);

useEffect(()=>{


},[showDialog,editedValue])
  return (
    <Box sx={licensePlate.mainWrapper}>
      <div
        ref={containerRef}
        onBlur={handleBlur}
        tabIndex={-1}
        style={licensePlate.wrapper}
      >
        <div style={licensePlate.iconWrapper}>{renderIcon()}</div>
        {isEditing ? (
         <Autocomplete
         sx={{minWidth:'130px', height:'30px',fontSize:'12px'}}
         onChange={handleAutocompleteChange} 
         PopperComponent={CustomPopper} 
         disableClearable={true}
         clearOnEscape
         forcePopupIcon="auto"
         value={editedValue}
         onInputChange={(event, newInputValue) => {
           handleInputChange({ target: { value: newInputValue } } as React.ChangeEvent<HTMLInputElement>);
         }}
         options={availablePlates}
         renderInput={(params) => (
           <TextField
           
             {...params}
             value={editedValue}
             onChange={handleInputChange} // Continua a gestire l'input normalmente
  onBlur={() => setShowDialog(true)}
             size="small"
             variant="standard"
               sx={driverDrawer.autocompleteComponent2}
           />
         )}
       />
        ) : (
          <Typography sx={getPlateStyle()}>
            {!editedValue || editedValue === '' || editedValue === '-' ? t("common.sidebars.addVehicle.addPlate") : editedValue}
          </Typography>
        )}
        {!isEditing &&<IconButton
          onClick={handleEditClick}
          size="small"
          sx={licensePlate.iconButton }
          disabled={isUpdatable === false || approvalStatus === ApprovalStatus.REJECTED || status === StatusOptions.COMPLETED || status === StatusOptions.WAITING_FOR_DOCUMENTATION || status === StatusOptions.IN_PROGRESS ? true:false}
        >
          <img
            src={modifyIcon}
            alt="Contracts Icon"
            style={{...licensePlate.icon, opacity:isUpdatable === false || approvalStatus===ApprovalStatus.REJECTED || status === StatusOptions.COMPLETED || status === StatusOptions.WAITING_FOR_DOCUMENTATION || status === StatusOptions.IN_PROGRESS ? 0.5:1}}
          />
        </IconButton>}

        {/* Traceability Popover */}
        <TraceabilityPopover
          open={traceabilityDialogOpen}
          anchorEl={anchorEl}
          onClose={() => {
            setTraceabilityDialogOpen(false);
            setAnchorEl(null);
          }}
          data={{
            licensePlate: editedValue,
            platform: traceabilityData?.platform,
            tripsInfo: traceabilityData?.tripsInfo
          }}
        />

        <Dialog open={showDialog} onClose={handleCancel}>
          <DialogTitle>
            {t("screens.trips.licensePlate.saveChanges")}
          </DialogTitle>
          <DialogContent>
            {t("screens.trips.licensePlate.saveOrDiscard")}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} color="secondary">
              {t("screens.trips.licensePlate.cancel")}
            </Button>
            <Button onClick={handleSave} color="primary" variant="contained">
              {t("screens.trips.licensePlate.save")}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Box>
  );
};

export default EditableLicensePlate;
